import {useEffect, useState} from 'react';

/**
 * Отслеживает видимость элемента через IntersectionObserver.
 * Принимает сам элемент, а не RefObject: запись в ref.current не вызывает
 * ре-рендер, и появление якоря в DOM было бы не отследить.
 */
export const useIsElementVisible = (element: HTMLElement | null, options?: IntersectionObserverInit): boolean => {
  const [isVisible, setIsVisible] = useState(false);

  const {root = null, rootMargin, threshold} = options ?? {};
  // Порог бывает массивом, а он по спецификации задаётся литералом: сравнение
  // по ссылке считало бы его новым на каждом рендере, наблюдатель пересоздавался
  // бы бесконечно, а его завершение каждый раз роняло бы видимость в false.
  const thresholdKey = Array.isArray(threshold) ? threshold.join(',') : threshold;

  useEffect(() => {
    if (element === null) {
      // Без сброса хук навсегда остался бы в последнем известном `true`:
      // «виден» относится к конкретному узлу, а наблюдать сейчас нечего.
      setIsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    }, {root, rootMargin, threshold});

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
      setIsVisible(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, root, rootMargin, thresholdKey]);

  return isVisible;
};
